import { createContext, useContext, type ReactNode } from 'react'
import type { Locale, PublicSiteContext, PublicSiteContextResponse } from '@seaa/shared'
import { migratePreviousLocaleStorage, validLocale } from './i18n'

export interface SeaaBootstrap {
  url: string
  origin: string
  hostname: string
  pathname: string
  locale: Locale
  now: string
  timeZone: string
  siteResolved: boolean
  site: PublicSiteContext | null
  customerAppOrigin?: string
  dehydratedState?: unknown
}

declare global {
  interface Window { __SEAA_BOOTSTRAP__?: SeaaBootstrap }
}

function browserBootstrap(): SeaaBootstrap {
  const storedLocale = migratePreviousLocaleStorage()

  const supplied = window.__SEAA_BOOTSTRAP__
  if (supplied) return supplied
  const encoded = document.getElementById('root')?.getAttribute('data-seaa-bootstrap')
  if (encoded) {
    try { return JSON.parse(decodeURIComponent(encoded)) as SeaaBootstrap } catch { /* fall through */ }
  }
  let siteContext: PublicSiteContextResponse | undefined
  const encodedContext = document.getElementById('root')?.getAttribute('data-seaa-site-context')
  if (encodedContext) {
    try { siteContext = JSON.parse(decodeURIComponent(encodedContext)) as PublicSiteContextResponse } catch { /* fetch on the client */ }
  }
  const requestedLocale = new URLSearchParams(window.location.search).get('lang')
  const locale: Locale = validLocale(requestedLocale) ? requestedLocale : validLocale(storedLocale) ? storedLocale : 'ar'
  return {
    url: window.location.href,
    origin: window.location.origin,
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    locale,
    now: new Date().toISOString(),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    siteResolved: Boolean(siteContext),
    site: siteContext?.site ?? null,
    ...(siteContext ? { customerAppOrigin: siteContext.customerAppOrigin } : {}),
  }
}

const fallback: SeaaBootstrap = {
  url: 'http://localhost/', origin: 'http://localhost', hostname: 'localhost', pathname: '/',
  locale: 'ar', now: new Date(0).toISOString(), timeZone: 'UTC', siteResolved: false, site: null,
}

const AppRuntimeContext = createContext<SeaaBootstrap>(fallback)

export function AppRuntimeProvider({ value, children }: { value?: SeaaBootstrap; children: ReactNode }) {
  const runtime = value ?? (typeof window === 'undefined' ? fallback : browserBootstrap())
  return <AppRuntimeContext.Provider value={runtime}>{children}</AppRuntimeContext.Provider>
}

export function useAppRuntime() {
  return useContext(AppRuntimeContext)
}

export function getBrowserBootstrap() {
  return browserBootstrap()
}
